// app/cell-format.ts — форматирование и разбор значений ячеек таблицы без React.
// Значения-узлы (булево, идентификатор, чип) форматируются в react/ и используют эти функции.

import { COLUMN_VALUE_FORMAT_OBJ, type ColumnFormatOptions, type ColumnValueFormat } from "../domain";

export const EMPTY_CELL = "—";

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

export function formatDateValue(v: unknown): string {
  if (!v) return EMPTY_CELL;
  const d = v instanceof Date ? v : new Date(String(v));
  if (Number.isNaN(d.getTime())) return String(v);
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
}

export function formatDateTimeValue(v: unknown): string {
  if (!v) return EMPTY_CELL;
  const d = v instanceof Date ? v : new Date(String(v));
  if (Number.isNaN(d.getTime())) return String(v);
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}

/** Shared: convert unknown to finite number, or null */
export function toNumber(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Shared: Intl.NumberFormat with decimals from ColumnFormatOptions */
export function makeNumberFormatter(
  opts: ColumnFormatOptions | undefined,
  locale: string,
): Intl.NumberFormat {
  const decimals = opts?.decimals ?? 2;
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function formatPercentValue(
  v: unknown,
  opts: ColumnFormatOptions | undefined,
  locale: string,
): string {
  const n = toNumber(v);
  if (n === null) return EMPTY_CELL;
  return `${makeNumberFormatter(opts, locale).format(n * 100)}%`;
}

export function formatNumberValue(
  v: unknown,
  opts: ColumnFormatOptions | undefined,
  locale: string,
): string {
  const n = toNumber(v);
  if (n === null) return EMPTY_CELL;
  const prefix = opts?.prefix ?? "";
  const suffix = opts?.suffix ?? "";
  return `${prefix}${makeNumberFormatter(opts, locale).format(n)}${suffix}`;
}

export function formatCurrencyValue(
  v: unknown,
  opts: ColumnFormatOptions | undefined,
  locale: string,
): string {
  const n = toNumber(v);
  if (n === null) return EMPTY_CELL;
  const prefix = opts?.prefix ?? "$";
  return `${prefix}${makeNumberFormatter(opts, locale).format(n)}`;
}

export function formatArrayValue(v: unknown, opts?: ColumnFormatOptions): string {
  if (!Array.isArray(v) || v.length === 0) return EMPTY_CELL;
  const sep = opts?.separator ?? ", ";
  const truncate = opts?.truncate;
  if (truncate !== undefined && v.length > truncate) {
    return `${v.length} items`;
  }
  return v.join(sep);
}

export function formatTextValue(v: unknown): string {
  return String(v ?? EMPTY_CELL);
}

/**
 * Convert raw value to editable string for <input> in bulk/inline edit mode.
 * percent: 0.05 → "5.00" (raw * 100, with decimals)
 * currency/number: 150 → "150.00" (with decimals, no prefix/suffix in value)
 * text/other: String(v)
 */
export function formatForEdit(
  v: unknown,
  format: ColumnValueFormat | undefined,
  opts: ColumnFormatOptions | undefined,
  locale: string,
): string {
  if (v === null || v === undefined) return "";
  switch (format) {
    case COLUMN_VALUE_FORMAT_OBJ.PERCENT: {
      const n = toNumber(v);
      if (n === null) return "";
      return makeNumberFormatter(opts, locale).format(n * 100);
    }
    case COLUMN_VALUE_FORMAT_OBJ.CURRENCY:
    case COLUMN_VALUE_FORMAT_OBJ.NUMBER: {
      const n = toNumber(v);
      if (n === null) return "";
      return makeNumberFormatter(opts, locale).format(n);
    }
    default:
      return String(v);
  }
}

/**
 * Parse editable string from <input> back to raw value for storage.
 * percent: "5.00" → 0.05 (Number(str) / 100)
 * currency/number: "150.00" → 150 (Number(str))
 * text/other: string as-is
 */
export function parseFromEdit(str: string, format?: ColumnValueFormat): unknown {
  switch (format) {
    case COLUMN_VALUE_FORMAT_OBJ.PERCENT: {
      const n = toNumber(str);
      return n !== null ? n / 100 : str;
    }
    case COLUMN_VALUE_FORMAT_OBJ.CURRENCY:
    case COLUMN_VALUE_FORMAT_OBJ.NUMBER: {
      const n = toNumber(str);
      return n !== null ? n : str;
    }
    default:
      return str;
  }
}
